// src/utils/announcer.js
//
// Saying things out loud to a dispatcher who is not looking at the screen.
//
// A dispatcher watches the load board, the phone, and three other tabs. The
// things worth interrupting them for — a driver going empty, a route shared, a
// matching load appearing — all happen on a screen they are not currently
// looking at. A toast in the corner of an unfocused tab is not an alert.
//
// On the voice: this uses the browser's own speech synthesis, which is free,
// needs no key, and works offline. It is deliberately behind a one-function
// seam (`speak`) so a nicer cloud voice can be swapped in later without any of
// the event logic changing.
//
// It will not imitate a real person. Voice cloning of an identifiable
// individual is prohibited by every major TTS provider and is covered by
// deepfake statutes in several US states; a neutral voice carries the same
// information and cannot be mistaken for a recording of somebody.

const STORAGE_KEY = 'loadmemo.announcer';

// Longer than any announcement takes to say, so a burst of related events does
// not become a queue the dispatcher has to sit through.
const MAX_QUEUE = 4;

/** Preference, remembered per browser. Never throws on blocked storage. */
export function getSettings() {
  const fallback = { enabled: false, volume: 0.9, rate: 1.0, voiceURI: null };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? { ...fallback, ...JSON.parse(raw) } : fallback;
  } catch {
    return fallback;
  }
}

export function saveSettings(next) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...getSettings(), ...next }));
  } catch {
    // Private window, or storage blocked. The setting lasts this session only,
    // which is better than refusing to speak at all.
  }
}

export function isSupported() {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

/**
 * The voices worth offering.
 *
 * Browsers expose dozens, most of them robotic. The neural ones have
 * recognisable names (Google …, Microsoft … Online, Siri …) and are the only
 * ones pleasant enough to listen to all day, so they are floated to the top
 * rather than hidden among the rest.
 */
export function listVoices() {
  if (!isSupported()) return [];
  const all = window.speechSynthesis.getVoices() || [];
  const english = all.filter(v => /^en(-|_|$)/i.test(v.lang));
  const nice = (v) => /google|natural|online|siri|premium|enhanced/i.test(v.name);
  return [...english.filter(nice), ...english.filter(v => !nice(v))];
}

/** A sensible default before anybody has chosen. */
export function defaultVoice() {
  return listVoices()[0] || null;
}

let speaking = false;
let queue = [];

function drain() {
  if (speaking || queue.length === 0 || !isSupported()) return;

  const { text, settings } = queue.shift();
  const utterance = new window.SpeechSynthesisUtterance(text);

  const voices = listVoices();
  const chosen = settings.voiceURI
    ? voices.find(v => v.voiceURI === settings.voiceURI)
    : null;
  if (chosen) utterance.voice = chosen;

  utterance.volume = settings.volume ?? 0.9;
  utterance.rate = settings.rate ?? 1.0;

  const next = () => { speaking = false; drain(); };
  utterance.onend = next;
  // A failed utterance must not wedge the queue shut — every later
  // announcement would be lost in silence with nothing to show for it.
  utterance.onerror = next;

  speaking = true;
  window.speechSynthesis.speak(utterance);
}

/**
 * Say something, if the dispatcher has the voice turned on.
 *
 * @param {string} text  plain words — no abbreviations the synthesiser will
 *   mangle. "I-95" is read as "eye ninety five"; write "Interstate 95".
 */
export function speak(text) {
  if (!text || !isSupported()) return;

  const settings = getSettings();
  if (!settings.enabled) return;

  // Drop the oldest rather than the newest. If five things happen at once the
  // dispatcher wants the latest, not a recital of the backlog.
  queue.push({ text, settings });
  if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE);

  drain();
}

/** Stop immediately and forget anything pending — used when muting. */
export function silence() {
  queue = [];
  speaking = false;
  if (isSupported()) {
    try { window.speechSynthesis.cancel(); } catch { /* nothing to cancel */ }
  }
}

/**
 * Say one line right now, ignoring the enabled setting.
 *
 * Only for the "test voice" button: somebody turning the feature on needs to
 * hear what they are turning on, and needs to discover a muted machine or a
 * missing voice then, rather than during the first real alert.
 */
export function preview(text) {
  if (!isSupported()) return;
  const settings = { ...getSettings(), enabled: true };
  queue.push({ text, settings });
  drain();
}
